import { Clear, Search } from '@mui/icons-material'
import { InputAdornment } from '@mui/material'
import React, { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { colors } from 'styles/variables'
import TooltipIconButton from 'ui/components/TooltipIconButton/TooltipIconButton'
import NewTextField from '../NewTextField/NewTextField'

type SearchFieldProps = {
  type?: 'text' | 'number'
  value?: string | number
  label?: string
  errorMessage?: string
  maxWidth?: string
  helperText?: string
  disabled?: boolean
  autoFocus?: boolean
  onDarkBackground?: boolean
  onChange: (value: string | number, e?: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  onEnter?: (value?: string) => void
  onEscape?: (value?: string) => void
  onClickAway?: (value?: string) => void
  onClear: () => void
  onBlur?: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  'data-testid'?: string
  inputProps?: Record<string, unknown>
}

export default function SearchField({
  type = 'text',
  value = undefined,
  label,
  errorMessage = '',
  helperText = '',
  disabled = false,
  autoFocus = false,
  maxWidth = '',
  onDarkBackground = false,
  onChange,
  onEnter,
  onEscape,
  onClickAway,
  onClear,
  onBlur,
  'data-testid': dataTestId,
  inputProps,
}: SearchFieldProps): ReactElement {
  const { t } = useTranslation()

  // Set default label if not provided
  const finalLabel = label ?? t('Search')

  const iconColor = disabled
    ? onDarkBackground
      ? colors.grey5
      : colors.grey3
    : onDarkBackground
      ? colors.white
      : colors.grey6

  const darkForegroundColor = disabled ? colors.grey5 : colors.white

  const darkBackgroundSx = onDarkBackground
    ? {
        '& .MuiOutlinedInput-input': { color: darkForegroundColor },
        '& .MuiOutlinedInput-input.Mui-disabled': {
          color: darkForegroundColor,
          WebkitTextFillColor: darkForegroundColor,
        },
        '& .MuiInputLabel-root': { color: darkForegroundColor },
        '& .MuiInputLabel-root.Mui-focused': { color: darkForegroundColor },
        '& .MuiInputLabel-root.Mui-disabled': { color: darkForegroundColor },
        '& .MuiOutlinedInput-notchedOutline': { borderColor: darkForegroundColor },
        '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: darkForegroundColor },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: darkForegroundColor },
        '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline': { borderColor: darkForegroundColor },
      }
    : undefined

  return (
    <NewTextField
      type={type}
      label={finalLabel}
      value={value}
      errorMessage={errorMessage}
      helperText={helperText}
      disabled={disabled}
      autoFocus={autoFocus}
      maxWidth={maxWidth}
      onChange={onChange}
      onEnter={onEnter ?? ((enterValue) => onChange(enterValue ?? ''))}
      onEscape={onEscape}
      onClickAway={onClickAway}
      onBlur={onBlur}
      data-testid={dataTestId}
      sx={[
        {
          '& .MuiOutlinedInput-input': { paddingLeft: '2px' },
          '& .MuiInputAdornment-positionStart': { marginLeft: '-4px', marginRight: '2px' },
          '& .MuiInputAdornment-positionEnd': { marginRight: '-6px' },
        },
        ...(darkBackgroundSx ? [darkBackgroundSx] : []),
      ]}
      inputProps={{
        ...inputProps,
        startAdornment: (
          <InputAdornment position="start">
            <Search fontSize="small" sx={{ color: iconColor }} />
          </InputAdornment>
        ),
        endAdornment: (
          <InputAdornment position="end">
            <TooltipIconButton
              icon={<Clear fontSize="small" />}
              iconColor={iconColor}
              iconSize={'small'}
              onClick={disabled ? undefined : onClear}
              tooltip={t('Clear search')}
              disabled={disabled}
            />
          </InputAdornment>
        ),
      }}
    />
  )
}
